/** The cumulative cash flow of one payment option, year 0 (the outlay) to the end of the analysis, with the payback
 * point. Inline SVG that scales to its box; values in a <title> per bar for hover and screen readers. */
import { useT } from "../i18n/I18nProvider";
import { money } from "../lib/format";
import { cashFlowChart, crossover } from "./payments.ts";
import type { S } from "../api/client";

export function CashFlowChart({ flows, currency, width = 320, height = 150, large }: { flows: S["SavingsCashFlowYear"][]; currency: string; width?: number; height?: number; large?: boolean }) {
  const t = useT();
  const plot = height - 22;
  const g = cashFlowChart(flows, width, plot, 6);
  const payback = crossover(flows);
  const last = flows[flows.length - 1];
  const ticks = flows.filter((f) => f.year % 5 === 0);
  const slot = flows.length ? width / flows.length : width;
  return (
    <figure className={`cashflow${large ? " large" : ""}`}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={payback != null ? t("editor.cashFlow.ariaPayback", { years: Math.max(0, flows.length - 1), payback: Math.round(payback * 10) / 10 }) : t("editor.cashFlow.aria", { years: Math.max(0, flows.length - 1) })}>
        <line x1={0} x2={width} y1={g.zeroY} y2={g.zeroY} className="cf-zero" />
        {g.bars.map((b) => (
          <rect key={b.year} x={b.x} y={b.y} width={b.w} height={b.h} rx={Math.min(3, b.w / 3)} className={b.positive ? "cf-pos" : "cf-neg"}>
            <title>{t("editor.cashFlow.bar", { year: b.year, amount: money(flows.find((f) => f.year === b.year)?.cumulative ?? 0, currency) })}</title>
          </rect>
        ))}
        {g.paybackX != null && <line x1={g.paybackX} x2={g.paybackX} y1={0} y2={plot} className="cf-payback" />}
        {ticks.map((t) => (
          <text key={t.year} x={(t.year - (flows[0]?.year ?? 0) + 0.5) * slot} y={height - 6} className="cf-tick">
            {t.year}
          </text>
        ))}
      </svg>
      <figcaption>
        <span>{payback != null ? t("editor.cashFlow.paysBack", { year: Math.round(payback * 10) / 10 }) : t("editor.cashFlow.noPayback")}</span>
        {last && <b>{t("editor.cashFlow.after", { amount: money(last.cumulative, currency), years: last.year })}</b>}
      </figcaption>
    </figure>
  );
}
